/*
 * SPDX-FileCopyrightText: © 2023 Schneider Electric
 *
 * SPDX-License-Identifier: LGPL-2.1-only
 */

:host {
  width: 100%;
  font-family: var(--qds-font-family-brand);
}

.divider-wrapper {
  display: flex;
  flex-direction: column;
}

.divider-gap {
  gap: var(--qds-list-item-standard-gap-internal);
}

.accordion-item {
  display: flex;
  flex-direction: column;
}

.header {
  margin-block: 0;
  transition: margin-block-end 0.2s ease-in-out;
}

.header-gap {
  margin-block-end: var(--qds-list-item-standard-gap-internal);
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  height: var(--qds-control-small-height);
  width: var(--qds-control-small-height);
  min-width: var(--qds-control-small-height);
  max-width: var(--qds-control-small-height);
  padding: 0;
  border-radius: var(--qds-control-border-radius);
}

button {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--qds-main-section-titles-icon-gap);
  padding: var(--qds-list-item-standard-padding-vertical)
    var(--qds-list-item-standard-padding-horizontal);
  border-radius: var(--qds-focus-border-radius);
  text-align: start;
  background-color: transparent;
  border: none;
  cursor: pointer;

  &:hover {
    .action-button {
      background: var(--qds-theme-interactive-background-hover);
    }
  }

  &:focus-visible {
    outline: var(--qds-theme-focus-border) solid var(--qds-focus-border-width);
    outline-offset: var(--qds-focus-border-offset);
  }
}

.title {
  color: var(--qds-theme-title);
  width: 100%;
}

.qds-counter {
  background-color: var(--qds-theme-feedback-message-neutral);
  color: var(--qds-theme-feedback-message-neutral-contrast);
  border-radius: var(--qds-control-rounded-border-radius);
  font: var(--qds-badge-standard-text);
  height: var(--qds-badge-counter-standard-height);
  padding-inline: var(--qds-badge-standard-padding-horizontal);
}

.qds-sr-only {
  clip: rect(0 0 0 0);
  height: 1px;
  width: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
}

qds-icon {
  height: var(--qds-control-large-icon-size);
  width: var(--qds-control-large-icon-size);
  transition-duration: 0.3s;
  transition-property: transform;
  color: var(--qds-theme-text-standard);
}

.open-icon {
  transform: rotate(180deg);
}

.details {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.2s ease-out;
  animation: hide-overflow 0s forwards;
  color: var(--qds-theme-text-standard);
}

.details-open {
  animation: show-overflow 0.3s forwards;
}

/* delay overflow property using animation */
@keyframes hide-overflow {
  from {
    overflow: hidden;
  }

  to {
    overflow: hidden;
  }
}

@keyframes show-overflow {
  from {
    overflow: hidden;
  }

  to {
    overflow: visible; /* Make overflow visible after expanding */
  }
}

.details-padding {
  padding: 0 var(--qds-list-item-standard-padding-horizontal);
}

qds-divider {
  width: 100%;
}

[data-layer="main"] {
  &[data-level="section"] {
    &.divider-gap {
      gap: var(--qds-list-item-large-gap-internal);
    }

    .header-gap {
      margin-block-end: var(--qds-list-item-large-gap-internal);
    }

    button {
      padding: var(--qds-list-item-large-padding-horizontal);
      gap: var(--qds-main-section-titles-icon-gap);
    }

    .title {
      font: var(--qds-main-section-title-emphasized);
    }

    .action-button {
      height: var(--qds-control-large-height);
      width: var(--qds-control-large-height);
      min-width: var(--qds-control-large-height);
      max-width: var(--qds-control-large-height);
    }

    .details-padding {
      padding: 0 var(--qds-list-item-large-padding-horizontal);
    }
  }

  &[data-level="subsection"] {
    button {
      gap: var(--qds-main-subsection-titles-icon-gap);
    }

    .title {
      font: var(--qds-main-subsection-title-emphasized);
    }
  }
}

[data-layer="panel"] {
  &[data-level="section"] {
    button {
      gap: var(--qds-panel-section-titles-icon-gap);
      padding: var(--qds-list-item-small-padding-vertical) 0;
    }

    .title {
      font: var(--qds-panel-section-title-emphasized);
    }

    .details-padding {
      padding: 0;
    }
  }

  &[data-level="subsection"] {
    &.divider-gap {
      gap: var(--qds-list-item-small-gap-internal);
    }

    .header-gap {
      margin-block-end: var(--qds-list-item-small-gap-internal);
    }

    button {
      gap: var(--qds-panel-subsection-titles-icon-gap);
      padding: var(--qds-list-item-small-padding-vertical) 0;
    }

    .title {
      font: var(--qds-panel-subsection-title-emphasized);
    }

    .details-padding {
      padding: 0;
    }
  }
}

[data-layer="popup"] {
  &[data-level="section"] {
    button {
      gap: var(--qds-popup-section-titles-icon-gap);
    }

    .title {
      font: var(--qds-popup-section-title-emphasized);
    }
  }

  &[data-level="subsection"] {
    button {
      padding: var(--qds-list-item-small-padding-vertical)
        var(--qds-list-item-small-padding-horizontal);
      gap: var(--qds-popup-subsection-titles-icon-gap);
    }

    .title {
      font: var(--qds-popup-subsection-title-emphasized);
    }

    .details-padding {
      padding: 0 var(--qds-list-item-small-padding-horizontal);
    }
  }
}

@media (width <= 1200px) {
  [data-layer="main"] {
    &[data-level="section"] {
      .title {
        font: var(--qds-main-section-title);
      }
    }

    &[data-level="subsection"] {
      .title {
        font: var(--qds-main-subsection-title);
      }
    }
  }

  [data-layer="panel"] {
    &[data-level="section"] {
      .title {
        font: var(--qds-panel-section-title);
      }
    }

    &[data-level="subsection"] {
      .title {
        font: var(--qds-panel-subsection-title);
      }
    }
  }

  [data-layer="popup"] {
    &[data-level="section"] {
      .title {
        font: var(--qds-popup-section-title);
      }
    }

    &[data-level="subsection"] {
      .title {
        font: var(--qds-popup-subsection-title);
      }
    }
  }
}
